/** LinkedIn-style Events feature (distinct from the analytics `events` table — see schemas/events.ts). */
import { z } from 'zod';
import { EpochMsSchema, SlugSchema, UlidSchema } from './common';

export const EventModeSchema = z.enum(['online', 'in_person']);
export const RsvpStatusSchema = z.enum(['yes', 'no', 'maybe']);

export const EventCreateSchema = z
  .object({
    title: z.string().min(2).max(160),
    description: z.string().max(4000).default(''),
    mode: EventModeSchema,
    location: z.string().max(300).default(''),
    onlineUrl: z.string().url().nullish(),
    startsAt: EpochMsSchema,
    endsAt: EpochMsSchema,
    orgId: UlidSchema.nullish(),
  })
  .refine((v) => v.endsAt > v.startsAt, { message: 'endsAt must be after startsAt', path: ['endsAt'] })
  .refine((v) => v.mode !== 'online' || !!v.onlineUrl, {
    message: 'onlineUrl is required for online events',
    path: ['onlineUrl'],
  });

export const EventPatchSchema = z
  .object({
    title: z.string().min(2).max(160),
    description: z.string().max(4000),
    location: z.string().max(300),
    onlineUrl: z.string().url().nullable(),
    startsAt: EpochMsSchema,
    endsAt: EpochMsSchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');

export const EventListingSchema = z.object({
  id: UlidSchema,
  slug: SlugSchema,
  title: z.string(),
  description: z.string(),
  mode: EventModeSchema,
  location: z.string(),
  onlineUrl: z.string().nullable(),
  startsAt: EpochMsSchema,
  endsAt: EpochMsSchema,
  hostUserId: UlidSchema,
  orgId: UlidSchema.nullable(),
  seoEnabled: z.boolean(),
  geoEnabled: z.boolean(),
  attendingCount: z.number().int().nonnegative(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
  viewerRsvp: RsvpStatusSchema.nullable().optional(),
});
export const EventListSchema = z.object({
  items: z.array(EventListingSchema),
  nextCursor: z.string().nullable(),
});
export const EventListQuerySchema = z.object({
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const EventIdParamsSchema = z.object({ id: UlidSchema });

export const RsvpInputSchema = z.object({ status: RsvpStatusSchema });
export const RsvpSchema = z.object({
  eventId: UlidSchema,
  userId: UlidSchema,
  status: RsvpStatusSchema,
  createdAt: EpochMsSchema,
});
/** Signed-in viewers only; `yes` RSVPs of people the viewer may see (N2-P4). */
export const AttendeeListSchema = z.object({
  items: z.array(z.object({ userId: UlidSchema, status: RsvpStatusSchema })),
});
